import { Spinner } from "@climatepartner/components/Spinner";
import { InvitationScreen } from "@service/network/supplier/dto/resolve-invite-response.dto";
import { ReactElement, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { formatLongDateOrPlaceholder } from "../../../../../shared/lib/format-date";
import {
  buildNetworkSupplierConnectionPath,
  buildNetworkSupplierSelectAccountPath,
  NETWORK_PATH,
} from "../../../../../shared/routing/paths/network";
import { useResolveInvite } from "../api/use-resolve-invite";
import { AccountCreationForm } from "./account-creation-form";
import { ErrorCard } from "./error-card";
import { toErrorCardVariant } from "./error-card.helpers";
import { BUYER_NAME_FALLBACK } from "./invitation-header-card";
import { PageChrome } from "./page-chrome";

export const SupplierLandingPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";
  const cr = searchParams.get("cr") ?? "";

  const { data, isLoading, error } = useResolveInvite(cr, token);

  // CONNECTION_READY means the supplier is already linked to exactly one account — nothing to pick.
  const isConnectionReady = data?.screen === InvitationScreen.CONNECTION_READY;

  // ACCOUNT_SELECTION means multiple accounts matched the invited email; the picker requires a real
  // authenticated session, which this public page cannot hydrate. The select-account route gets
  // cr/token so it can re-resolve and commit as the logged-in user.
  const shouldRedirectToAccountSelection = data?.screen === InvitationScreen.ACCOUNT_SELECTION;

  // ALREADY_CONNECTED means a stale link: the PCF was submitted and the token cleared. The connection
  // page is login-gated, so the app's auth gate takes over from here.
  const isAlreadyConnected = data?.screen === InvitationScreen.ALREADY_CONNECTED;

  useEffect(() => {
    if (isAlreadyConnected) {
      navigate(buildNetworkSupplierConnectionPath(cr), { replace: true });
    }
  }, [isAlreadyConnected, cr, navigate]);

  useEffect(() => {
    if (isConnectionReady) {
      navigate(NETWORK_PATH, { replace: true });
    }
  }, [isConnectionReady, navigate]);

  useEffect(() => {
    if (shouldRedirectToAccountSelection) {
      navigate(buildNetworkSupplierSelectAccountPath(cr, token), { replace: true });
    }
  }, [shouldRedirectToAccountSelection, cr, token, navigate]);

  if (!token || !cr || error) {
    return (
      <PageChrome>
        <ErrorCard variant={toErrorCardVariant(error)} />
      </PageChrome>
    );
  }

  // Spinner while resolving, and while the CONNECTION_READY / ALREADY_CONNECTED / ACCOUNT_SELECTION effects redirect.
  if (
    isLoading ||
    !data ||
    data.screen === InvitationScreen.CONNECTION_READY ||
    data.screen === InvitationScreen.ALREADY_CONNECTED ||
    data.screen === InvitationScreen.ACCOUNT_SELECTION
  ) {
    return (
      <PageChrome>
        <Spinner />
      </PageChrome>
    );
  }

  const { invitation } = data;

  // Only SELF_REGISTRATION renders inline on this public page; the other screens redirect above.
  return (
    <PageChrome>
      <AccountCreationForm
        token={token}
        buyerName={invitation.buyerName ?? BUYER_NAME_FALLBACK}
        requestDueDate={formatLongDateOrPlaceholder(invitation.respondBy)}
        connectionRequestId={cr}
      />
    </PageChrome>
  );
};
